import Panzoom from '@panzoom/panzoom';

const SVG_NS = 'http://www.w3.org/2000/svg';
let nextMapId = 0;

function element(name, attributes = {}) {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
  return node;
}

const currencyFormat = new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'});
function money(value) { return currencyFormat.format(Number(value || 0)); }

// "K9" before "K10": compare digit runs numerically, the rest as text.
function compareSeatCodes(left, right) {
  const a = String(left || '').match(/\d+|\D+/g) || [];
  const b = String(right || '').match(/\d+|\D+/g) || [];
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const x = a[index], y = b[index];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (/^\d+$/.test(x) && /^\d+$/.test(y)) {
      if (Number(x) !== Number(y)) return Number(x) - Number(y);
    } else if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

// Buyers read their selection by sector, then row, then seat — never click order.
// Mirrors seat_reservations.seat_item_sort_key so both renderings agree.
function compareSeatItems(a, b) {
  const sponsorA = String(a.sponsor_name || ''), sponsorB = String(b.sponsor_name || '');
  return (Number(a.block_order || 0) - Number(b.block_order || 0))
    || (sponsorA === sponsorB ? 0 : (sponsorA < sponsorB ? -1 : 1))
    || (Number(a.seat_line || 0) - Number(b.seat_line || 0))
    || (Number(a.seat_position || 0) - Number(b.seat_position || 0))
    || compareSeatCodes(a.seat_code, b.seat_code);
}
window.compareSeatItems = compareSeatItems;

function createSeatBatchQueue() {
  const waiting = [];
  let running = false;
  async function drain() {
    try {
      while (waiting.length) {
        const first = waiting[0];
        const entries = [];
        while (waiting.length && entries.length < 6 && waiting[0].key === first.key) {
          const entry = waiting.shift();
          if (entry.valid()) entries.push(entry);
          else entry.cancel();
        }
        if (entries.length) {
          try { await first.send(entries); }
          catch (_) { entries.forEach(entry => entry.cancel()); }
        }
      }
    } finally { running = false; }
  }
  return {add(entry) {
    waiting.push(entry);
    if (!running) {
      running = true;
      // Share ordering with the legacy controls and with other session maps.
      window._seatChangeQueue = (window._seatChangeQueue || Promise.resolve()).then(drain, drain);
    }
  }};
}

function initSeatNavigation(root, getCount) {
  const modal = root.closest?.('.seat-map-modal');
  const button = modal?.querySelector('[data-seat-navigation]');
  const body = modal?.querySelector('.modal-body');
  const list = root.querySelector('[data-seat-selection-section]');
  const map = root.querySelector('[data-seat-viewport]');
  if (!button || !body || !list || !map) return () => {};
  button._seatNavigationCleanup?.();
  let showingList = false;
  const update = () => {
    const bounds = body.getBoundingClientRect();
    const section = list.getBoundingClientRect();
    showingList = section.top < bounds.bottom - 40 && section.bottom > bounds.top;
    button.textContent = showingList ? button.dataset.backToMap
      : button.dataset.viewTickets.replace('{count}', String(getCount()));
    button.hidden = false;
  };
  const navigate = () => {
    const target = showingList ? map : list;
    const top = body.scrollTop + target.getBoundingClientRect().top - body.getBoundingClientRect().top;
    body.scrollTo({top: Math.max(0, top - 8),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    target.focus({preventScroll: true});
  };
  button.addEventListener('click', navigate);
  body.addEventListener('scroll', update, {passive: true});
  modal.addEventListener('shown.bs.modal', update);
  window.addEventListener('resize', update);
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
  observer?.observe(body);
  observer?.observe(list);
  const cleanup = () => {
    button.removeEventListener('click', navigate);
    body.removeEventListener('scroll', update);
    modal.removeEventListener('shown.bs.modal', update);
    window.removeEventListener('resize', update);
    observer?.disconnect();
    button.hidden = true;
    delete button._seatNavigationCleanup;
  };
  button._seatNavigationCleanup = cleanup;
  root._seatNavigationCleanup = cleanup;
  update();
  return update;
}

window.initSeatMap = function initSeatMap(root) {
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = '1';
  // Keep writes local: body.htmx-request opens the page-wide blocking overlay.
  root.setAttribute('hx-indicator', 'this');
  const readOnly = root.dataset.readonly === '1';
  const t = (key, values = {}) => root.dataset[`i18n${key[0].toUpperCase()}${key.slice(1)}`]
    .replace(/\{(\w+)\}/g, (_, name) => String(values[name]));
  const geometry = JSON.parse(root.querySelector('[data-seat-geometry]').textContent);
  const svg = root.querySelector('[data-seat-svg]');
  // Clip the stroke to the dot itself, keeping every state inside the same footprint.
  const dotClipId = `seat-dot-clip-${++nextMapId}`;
  const defs = element('defs');
  const dotClip = element('clipPath', {id: dotClipId, clipPathUnits: 'objectBoundingBox'});
  dotClip.appendChild(element('circle', {cx: '.5', cy: '.5', r: '.5'}));
  defs.appendChild(dotClip);
  svg.appendChild(defs);
  const canvas = root.querySelector('[data-seat-canvas]');
  const tooltip = root.querySelector('[data-seat-tooltip]');
  const selectionList = root.querySelector('[data-seat-selection-list]');
  const feedback = root.querySelector('[data-seat-feedback]');
  let selectedItems = JSON.parse(root.querySelector('[data-seat-selected-items]').textContent || '[]');
  const selected = new Set(JSON.parse(root.dataset.selected || '[]'));
  const updateSeatNavigation = readOnly ? () => {} : initSeatNavigation(root, () => selected.size);
  const unavailable = new Set(geometry.filter(seat => seat.unavailable).map(seat => seat.allocation_id));
  const nodes = new Map();
  const seatsByBlock = new Map();
  const blockBounds = new Map();
  const pending = new Set();
  let statusVersion = 0;
  let statusReady = false;
  let availabilityError = false;
  let poller = null;
  let geometryReloading = false;
  let previousVisual = null;
  let checkout = null;
  let batchGeneration = 0;
  let pointerStart = null;
  let dragged = false;
  let restoreFocusKey = null;
  let courtesyRemaining = Number(root.dataset.courtesyRemaining || 0);
  let serverOffset = Date.parse(root.dataset.serverNow || '') - Date.now();

  // Limit targets by the nearest seat, so enlarging them never steals a neighbour's tap.
  const targetGrid = new Map();
  geometry.forEach(seat => {
    const key = `${Math.floor(seat.x / 2.2)},${Math.floor(seat.y / 2.2)}`;
    if (!targetGrid.has(key)) targetGrid.set(key, []);
    targetGrid.get(key).push(seat);
  });
  function seatHitRadius(seat) {
    let nearest = 2.2;
    const cellX = Math.floor(seat.x / 2.2), cellY = Math.floor(seat.y / 2.2);
    for (let x = cellX - 1; x <= cellX + 1; x += 1) {
      for (let y = cellY - 1; y <= cellY + 1; y += 1) {
        for (const other of targetGrid.get(`${x},${y}`) || []) {
          if (other !== seat) nearest = Math.min(nearest, Math.hypot(seat.x - other.x, seat.y - other.y));
        }
      }
    }
    return nearest * .49;
  }
  const hitRadii = new Map(geometry.map(seat => [seat, seatHitRadius(seat)]));
  // All dots share one radius, sized for the tightest spacing in this map.
  const dotRadius = geometry.reduce((radius, seat) => Math.min(radius, hitRadii.get(seat) * .44 / .49), .74);

  geometry.forEach((seat) => {
    const group = element('g', {
      class: 'seat-node', tabindex: '0', role: 'button',
      'data-allocation-id': seat.allocation_id,
    });
    const hitTarget = element('circle', {class: 'seat-hit-target', cx: seat.x, cy: seat.y, r: hitRadii.get(seat)});
    const circle = element('circle', {class: 'seat-dot', cx: seat.x, cy: seat.y, r: dotRadius, 'clip-path': `url(#${dotClipId})`});
    const symbol = element('text', {x: seat.x, y: seat.y + .18, 'text-anchor': 'middle'});
    symbol.textContent = seat.type === 'C' ? '♿' : (seat.type === 'A' ? 'A' : (seat.type === 'O' ? 'O' : ''));
    group.append(hitTarget, circle, symbol);
    svg.appendChild(group);
    symbol.style.fill = seat.text_color;
    nodes.set(seat.allocation_id, {group, circle, symbol, seat});
    const blockSeats = seatsByBlock.get(seat.block_id) || [];
    blockSeats.push(seat);
    seatsByBlock.set(seat.block_id, blockSeats);
    group.addEventListener('mouseenter', (event) => showTooltip(event, seat));
    group.addEventListener('mousemove', (event) => positionTooltip(event));
    group.addEventListener('mouseleave', () => tooltip.classList.add('d-none'));
    group.addEventListener('click', () => { if (!dragged) return chooseSeat(seat); });
    group.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); return chooseSeat(seat); }
    });
  });

  // Public sector names follow the physical geometry and zoom with their seats.
  seatsByBlock.forEach((seats, blockId) => {
    const xs = seats.map(seat => seat.x), ys = seats.map(seat => seat.y);
    const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const labelWidth = Math.max(9, Math.max(...xs) - Math.min(...xs) + 1);
    const maxChars = Math.max(8, Math.floor(labelWidth / 1.05));
    const name = String(seats[0].sponsor || '').trim();
    const lines = [];
    name.split(/\s+/).forEach(word => {
      // Wrap long names, including words longer than narrow side sectors.
      for (let start = 0; start < word.length; start += maxChars) {
        const part = word.slice(start, start + maxChars);
        const last = lines.length - 1;
        if (last >= 0 && lines[last].length + part.length + 1 <= maxChars) lines[last] += ` ${part}`;
        else lines.push(part);
      }
    });
    const labelY = Math.min(...ys) - 1.6 - Math.max(0, lines.length - 1) * 2.1;
    const heading = element('text', {
      class: 'seat-sector-label', 'data-sector-label': blockId,
      x: centerX, y: labelY, 'text-anchor': 'middle', 'aria-label': name,
    });
    lines.forEach((line, index) => {
      const span = element('tspan', {x: centerX, y: labelY + index * 2.1});
      span.textContent = line; heading.appendChild(span);
    });
    svg.appendChild(heading);
    blockBounds.set(blockId, {
      minX: Math.min(Math.min(...xs), centerX - labelWidth / 2) - 2,
      minY: labelY - 2,
      maxX: Math.max(Math.max(...xs), centerX + labelWidth / 2) + 2,
      maxY: Math.max(...ys) + 2,
    });
  });
  const bounds = [...blockBounds.values()];
  const fullMinX = Math.min(0, ...bounds.map(b => b.minX));
  const fullMinY = Math.min(0, ...bounds.map(b => b.minY));
  const fullViewBox = `${fullMinX} ${fullMinY} ${Math.max(100, ...bounds.map(b => b.maxX)) - fullMinX} ${Math.max(100, ...bounds.map(b => b.maxY)) - fullMinY}`;
  svg.setAttribute('viewBox', fullViewBox);

  canvas.addEventListener('pointerdown', (event) => {
    if (pointerStart) { dragged = true; return; }
    pointerStart = {x: event.clientX, y: event.clientY}; dragged = false;
  });
  canvas.addEventListener('pointermove', (event) => {
    if (pointerStart && Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 6) dragged = true;
  });
  canvas.addEventListener('pointerup', () => { pointerStart = null; });
  canvas.addEventListener('pointercancel', () => { pointerStart = null; dragged = true; });

  const panzoom = Panzoom(canvas, {maxScale: 12, minScale: 1, contain: 'outside', cursor: 'grab'});
  root.querySelector('[data-seat-viewport]').addEventListener('wheel', panzoom.zoomWithWheel, {passive: false});
  root.querySelector('[data-zoom-in]').addEventListener('click', panzoom.zoomIn);
  root.querySelector('[data-zoom-out]').addEventListener('click', panzoom.zoomOut);
  root.querySelector('[data-reset]').addEventListener('click', () => {
    svg.setAttribute('viewBox', fullViewBox);
    panzoom.reset({animate: true});
  });
  root.querySelector('[data-block-fit]').addEventListener('change', (event) => {
    const box = blockBounds.get(event.target.value);
    if (!box) return;
    svg.setAttribute('viewBox', `${box.minX} ${box.minY} ${box.maxX - box.minX} ${box.maxY - box.minY}`);
    panzoom.reset();
  });

  root._seatView = () => ({map: root.dataset.seatMapId, viewBox: svg.getAttribute('viewBox'),
    scale: panzoom.getScale(), pan: panzoom.getPan(),
    focus: root.contains(document.activeElement) ? document.activeElement?.dataset.allocationId : null});
  root._restoreSeatView = view => {
    if (!view || view.map !== root.dataset.seatMapId) return;
    svg.setAttribute('viewBox', view.viewBox);
    requestAnimationFrame(() => {
      if (!root.isConnected) return;
      panzoom.zoom(view.scale, {animate: false});
      panzoom.pan(view.pan.x, view.pan.y, {animate: false});
      if (view.focus) nodes.get(view.focus)?.group.focus({preventScroll: true});
    });
  };
  root._geometryReloading = value => { geometryReloading = value; renderState(); };
  root._disposeSeatMap = () => {
    window.clearInterval(root._seatStatusTimer); window.clearInterval(root._seatCountdownTimer);
    panzoom.destroy(); statusVersion += 1;
  };

  function seatTypeLabel(seat) {
    const typeLabels = {N: t('standard'), O: t('oversized'), C: t('wheelchair'), A: t('companion')};
    return typeLabels[seat.type] || seat.type;
  }

  function label(seat, state) {
    const item = selectedItems.find(value => value.seat_allocation_id === seat.allocation_id);
    const stateLabels = {selected: t('selected'), unavailable: t('unavailable'), available: t('available')};
    return t('seatLabel', {sponsor: seat.sponsor, code: seat.code, type: seatTypeLabel(seat), price: money(item ? item.price : seat.price), state: stateLabels[state]});
  }

  function showTooltip(event, seat) {
    tooltip.textContent = t('tooltip', {sponsor: seat.sponsor, code: seat.code, type: seatTypeLabel(seat), price: money(seat.price)});
    tooltip.classList.remove('d-none'); positionTooltip(event);
  }
  function positionTooltip(event) {
    const rect = root.getBoundingClientRect();
    tooltip.style.left = `${event.clientX - rect.left + 12}px`;
    tooltip.style.top = `${event.clientY - rect.top + 12}px`;
  }

  async function chooseSeat(seat) {
    if (readOnly) {
      feedback.textContent = label(seat, unavailable.has(seat.allocation_id) ? 'unavailable' : 'available');
      return;
    }
    if (window.ticketDialogOpen) return;
    if (!statusReady || checkoutLocked() || unavailable.has(seat.allocation_id) || pending.has(seat.allocation_id)) return;
    if (selected.has(seat.allocation_id)) {
      return removeSeat(seat, nodes.get(seat.allocation_id).group);
    }
    changeSeat(seat, 1, 'full');
  }

  async function removeSeat(seat, source) {
    if (selectedItems.some(item => item.seat_allocation_id === seat.allocation_id && item.administrative_reserved)) return;
    if (readOnly || window.ticketDialogOpen || !statusReady || checkoutLocked()
        || pending.has(seat.allocation_id) || !selected.has(seat.allocation_id)) return;
    const message = ['C', 'A'].includes(seat.type)
      ? t('removePairConfirm', {sponsor: seat.sponsor, code: seat.code})
      : t('removeConfirm', {sponsor: seat.sponsor, code: seat.code});
    tooltip.classList.add('d-none');
    const revision = JSON.stringify(checkout);
    if (!await window.ticketConfirm(message, source)) return;
    if (!root.isConnected) return;
    if (!statusReady || !selected.has(seat.allocation_id) || revision !== JSON.stringify(checkout)) {
      feedback.textContent = t('notConfirmed');
      return;
    }
    changeSeat(seat, 0, 'full');
  }

  function changeSeat(seat, qty, fareType) {
    if (readOnly) return;
    const id = seat.allocation_id;
    if (pending.has(id) || checkoutLocked()) return;
    pending.add(id);
    poller?.beginWrite();
    statusVersion += 1;
    feedback.textContent = t('saving');
    renderState();
    if (root.dataset.seatBatch === '1' && checkout?.shared_checkout_id && poller && window.crypto?.randomUUID) {
      const generation = batchGeneration;
      const finish = () => { poller.endWrite(); pending.delete(id); renderState(); };
      const queue = window._cmpSeatBatchQueue ||= createSeatBatchQueue();
      queue.add({key: root, valid: () => root.isConnected && generation === batchGeneration && !checkoutLocked(),
        cancel: () => { feedback.textContent = t('notConfirmed'); finish(); },
        values: {ticket_class: root.dataset.ticketClass, seat_allocation_id: id, fare_type: fareType, qty},
        finish, seat, send: sendSeatBatch});
      return;
    }
    // Serialize local writes; the server also locks/version-checks shared carts.
    window._seatChangeQueue = (window._seatChangeQueue || Promise.resolve()).then(async () => {
      try {
        await htmx.ajax('POST', root.dataset.changeUrl, {
          source: root, target: '#ticket_results', swap: 'innerHTML',
          values: {ticket_class: root.dataset.ticketClass, seat_allocation_id: id, fare_type: fareType, qty,
            ...(checkout ? {shared_checkout_id: checkout.shared_checkout_id, shared_checkout_revision: checkout.shared_checkout_revision} : {})},
        });
        const updated = poller?.applyAction() || (!poller && await refreshStatus());
        const item = selectedItems.find((value) => value.seat_allocation_id === id);
        const accepted = updated && (qty ? selected.has(id) && (item?.fare_type === fareType || (item?.fare_locked && item?.fare_type === 'half') || ['invite_courtesy', 'companion_free'].includes(item?.fare_type)) : !selected.has(id));
        const serverError = document.getElementById('ticket_results')?.querySelector('[data-shared-checkout-error]');
        feedback.textContent = serverError?.textContent || (accepted ? t('updated', {code: seat.code}) : t('notConfirmed'));
        if (['invite_purchase_limit_reached', 'seat_order_limit_reached'].includes(serverError?.dataset.sharedCheckoutError)) {
          tooltip.classList.add('d-none');
          window.ticketPurchaseLimit(serverError.textContent, nodes.get(id).group,
            serverError.dataset.sharedCheckoutError, canFinishSelection());
        }
      } catch (_) {
        feedback.textContent = t('connectionError');
        if (!poller) await refreshStatus();
      } finally {
        poller?.endWrite();
        pending.delete(id);
        renderState();
      }
    });
  }

  async function sendSeatBatch(entries) {
    const batchId = window.crypto.randomUUID();
    const values = {seat_batch_id: batchId,
      seat_operations: JSON.stringify(entries.map(entry => entry.values)),
      shared_checkout_id: checkout.shared_checkout_id,
      shared_checkout_revision: checkout.shared_checkout_revision};
    let result;
    let applied = false;
    root.setAttribute('hx-request', JSON.stringify({timeout: 10000}));
    try {
      // A lost response retries the exact request and receipt IDs, once.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          await htmx.ajax('POST', root.dataset.changeUrl, {
            source: root, target: '#ticket_results', swap: 'innerHTML', values,
          });
          result = JSON.parse(document.getElementById('seat_batch_results')?.textContent || 'null');
          if (result?.batch_id !== batchId || result.operations?.length !== entries.length) throw new Error('Missing batch result');
          break;
        } catch (error) { if (attempt === 1) throw error; }
      }
      if (!poller.applyAction()) throw new Error('Missing checkout state');
      applied = true;
      const notices = [];
      result.operations.forEach((operation, index) => {
        const entry = entries[index];
        notices.push(operation.accepted ? t('updated', {code: entry.seat.code})
          : `${entry.seat.code}: ${operation.message || t('notConfirmed')}`);
        if (operation.code === 'seat_conflict') unavailable.add(entry.values.seat_allocation_id);
        if (['stale_checkout', 'batch_stopped', 'invalid_batch'].includes(operation.code)) batchGeneration += 1;
        if (['invite_purchase_limit_reached', 'seat_order_limit_reached'].includes(operation.code)) {
          window.ticketPurchaseLimit(operation.message, nodes.get(entry.values.seat_allocation_id).group,
            operation.code, canFinishSelection());
        }
      });
      feedback.textContent = notices.join(' ');
    } catch (_) {
      batchGeneration += 1;
      statusReady = false;
      feedback.textContent = t('connectionError');
    } finally {
      entries.forEach(entry => entry.finish());
      // The load probe measures each click, including retries and UI readiness.
      if (typeof CustomEvent !== 'undefined') document.dispatchEvent(new CustomEvent('cmp:seat-batch-result', {
        detail: {batch_id: batchId, operations: entries.map((entry, index) => ({
          ...entry.values, ...(applied ? result.operations[index] : {accepted: false, code: 'uncertain'}),
        }))},
      }));
    }
  }

  function renderSelection() {
    if (readOnly) return;
    // Preserve focused controls when the selection has not changed.
    const displayItems = [...selectedItems].sort(compareSeatItems);
    const signature = JSON.stringify([displayItems, [...pending], checkoutLocked()]);
    if (selectionList.dataset.signature === signature) return;
    selectionList.dataset.signature = signature;
    const focused = document.activeElement;
    const focusKey = selectionList.contains(focused) ? focused.dataset.controlKey : restoreFocusKey;
    selectionList.replaceChildren();
    let focusTarget = null;
    displayItems.forEach((item) => {
      const geometryMissing = !nodes.has(item.seat_allocation_id);
      const seat = nodes.get(item.seat_allocation_id)?.seat || {allocation_id: item.seat_allocation_id,
        code: item.seat_code, type: item.seat_type, sponsor: item.sponsor_name,
        color: item.sponsor_color || '#7d8590', price: item.price};
      const row = document.createElement('li'); row.className = 'seat-selection-row';
      const title = document.createElement('div'); title.className = 'fw-semibold';
      const sectorColor = document.createElement('span'); sectorColor.className = 'seat-sector-swatch';
      sectorColor.style.backgroundColor = seat.color;
      sectorColor.setAttribute('aria-hidden', 'true');
      const titleText = document.createElement('span');
      titleText.textContent = `${item.sponsor_name} · ${item.seat_code}`;
      title.append(sectorColor, titleText);
      const detail = document.createElement('div'); detail.className = 'small text-muted';
      detail.textContent = `${{N: t('standard'), O: t('oversized'), C: t('wheelchairPair'), A: t('companionPair')}[item.seat_type]} · ${money(item.price)}`;
      const info = document.createElement('div'); info.className = 'seat-selection-info'; info.append(title, detail); row.append(info);
      const controls = document.createElement('div'); controls.className = 'seat-selection-controls';
      if (['invite_courtesy', 'companion_free'].includes(item.fare_type)) {
        const badge = document.createElement('span'); badge.className = 'badge bg-success';
        badge.textContent = item.fare_type === 'invite_courtesy' ? t('courtesy') : t('freeCompanion'); controls.append(badge);
      } else if (item.fare_locked) {
        const badge = document.createElement('span'); badge.className = 'badge bg-secondary';
        badge.textContent = t('half'); controls.append(badge);
      } else {
        const fare = document.createElement('select'); fare.className = 'form-select form-select-sm w-auto';
        fare.setAttribute('aria-label', t('fareLabel', {code: seat.code}));
        fare.dataset.controlKey = `fare-${seat.allocation_id}`;
        [['full', t('full'), seat.price], ...(root.dataset.allowHalf === '1' ? [['half', t('half'), Number(seat.price) / 2]] : [])].forEach(([value, text, price]) => {
          const option = document.createElement('option'); option.value = value; option.textContent = `${text} ${money(price)}`; fare.append(option);
        });
        fare.value = item.fare_type; fare.disabled = geometryMissing || checkoutLocked() || pending.has(seat.allocation_id);
        fare.addEventListener('change', () => changeSeat(seat, 1, fare.value)); controls.append(fare);
        if (focusKey === fare.dataset.controlKey) focusTarget = fare;
      }
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'btn btn-sm btn-outline-danger';
      remove.textContent = ['C', 'A'].includes(seat.type) ? t('removePair') : t('remove');
      remove.setAttribute('aria-label', `${remove.textContent}: ${seat.code}`);
      remove.dataset.controlKey = `remove-${seat.allocation_id}`;
      remove.disabled = Boolean(item.administrative_reserved) || geometryMissing || checkoutLocked() || pending.has(seat.allocation_id);
      if (item.administrative_reserved) remove.title = 'Reservado pelo operador';
      remove.addEventListener('click', () => removeSeat(seat, remove)); controls.append(remove);
      if (focusKey === remove.dataset.controlKey) focusTarget = remove;
      row.append(controls); selectionList.append(row);
    });
    root.querySelector('[data-selection-count]').textContent = String(selected.size);
    updateSeatNavigation();
    root.querySelector('[data-selection-empty]').classList.toggle('d-none', selected.size > 0);
    restoreFocusKey = focusTarget?.disabled ? focusKey : null;
    if (focusTarget && !focusTarget.disabled) focusTarget.focus({preventScroll: true});
  }

  function renderState() {
    const locked = checkoutLocked();
    const prices = new Map(selectedItems.map(item => [String(item.seat_allocation_id), item.price]));
    let dirty;
    if (!previousVisual || previousVisual.locked !== locked) dirty = new Set(nodes.keys());
    else {
      dirty = new Set();
      [[selected, previousVisual.selected], [unavailable, previousVisual.unavailable],
       [pending, previousVisual.pending]].forEach(([current, previous]) => {
        current.forEach(id => { if (!previous.has(id)) dirty.add(id); });
        previous.forEach(id => { if (!current.has(id)) dirty.add(id); });
      });
      prices.forEach((price, id) => { if (previousVisual.prices.get(id) !== price) dirty.add(id); });
      previousVisual.prices.forEach((_, id) => { if (!prices.has(id)) dirty.add(id); });
    }
    previousVisual = {locked, prices, selected: new Set(selected), unavailable: new Set(unavailable), pending: new Set(pending)};
    dirty.forEach(id => {
      const node = nodes.get(id);
      if (!node) return;
      const {group, circle, symbol, seat} = node;
      const state = selected.has(id) ? 'selected' : (unavailable.has(id) ? 'unavailable' : 'available');
      const signature = JSON.stringify([state, pending.has(id), locked, prices.get(id)]);
      if (group.dataset.stateSignature === signature) return;
      group.dataset.stateSignature = signature;
      group.setAttribute('class', `seat-node ${state}${pending.has(id) ? ' pending' : ''}`);
      group.setAttribute('aria-label', label(seat, state));
      if (!readOnly) {
        group.setAttribute('aria-pressed', selected.has(id) ? 'true' : 'false');
        group.setAttribute('aria-busy', pending.has(id) ? 'true' : 'false');
        group.setAttribute('aria-disabled', locked || unavailable.has(id) ? 'true' : 'false');
      }
      circle.style.fill = state === 'selected' ? '#172b4d' : (state === 'unavailable' ? '#7d8590' : seat.color);
      circle.style.color = circle.style.fill;
      symbol.textContent = state === 'selected' ? '✓' : ({C: '♿', A: 'A', O: 'O'}[seat.type] || '');
      symbol.style.fill = state === 'available' ? seat.text_color : '#ffffff';
    });
    const button = document.getElementById(`seat_select_button_${root.dataset.ticketClass}`);
    if (button) button.textContent = t('select') + (selected.size ? ` (${selected.size})` : '');
    renderSelection();
  }

  function renderCountdown() {
    if (readOnly) return;
    const alert = root.querySelector('[data-seat-countdown-alert]');
    const output = root.querySelector('[data-seat-countdown]');
    const expires = Date.parse(root.dataset.expiresAt || '');
    // Preserve the timer's space even before the first reservation or after removal.
    if (!selected.size || !Number.isFinite(expires)) { alert.classList.add('invisible'); return; }
    alert.classList.remove('invisible');
    const remaining = Math.max(0, expires - (Date.now() + serverOffset));
    const seconds = Math.ceil(remaining / 1000);
    output.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    alert.classList.toggle('alert-warning', seconds > 0 && seconds <= 120);
    alert.classList.toggle('alert-info', seconds > 120);
    alert.classList.toggle('alert-danger', seconds === 0);
  }

  function checkoutLocked() {
    return geometryReloading || Boolean(checkout?.shared_checkout_id && !checkout.shared_checkout_editable);
  }

  // Offer "finish selection" from a limit dialog only while this map holds a
  // confirmable selection; otherwise the dialog keeps a single dismiss button.
  function canFinishSelection() {
    return selected.size > 0 && !checkoutLocked();
  }

  function applyStatus(data) {
    statusReady = true;
    if (checkout && data.checkout && checkout.shared_checkout_id === data.checkout.shared_checkout_id
        && Number(data.checkout.shared_checkout_revision) < Number(checkout.shared_checkout_revision)) return false;
    checkout = data.checkout || null;
    if (data.error) { availabilityError = true; feedback.textContent = t('connectionError'); }
    else if (data.unavailable && availabilityError) { availabilityError = false; feedback.textContent = ''; }
    if (checkoutLocked()) feedback.textContent = t('checkoutLocked');
    if (data.unavailable) { unavailable.clear(); data.unavailable.forEach((id) => unavailable.add(String(id))); }
    selectedItems = (data.selected_items || []).filter(item => item.ticket_class_id
      ? String(item.ticket_class_id) === root.dataset.ticketClass : nodes.has(String(item.seat_allocation_id)));
    selected.clear(); selectedItems.forEach(item => selected.add(String(item.seat_allocation_id)));
    root.dataset.expiresAt = data.expires_at || '';
    root.dataset.serverNow = data.server_now;
    if ('courtesy_remaining' in data) courtesyRemaining = Number(data.courtesy_remaining || 0);
    root.dataset.courtesyRemaining = String(courtesyRemaining);
    const courtesyOutput = root.querySelector('[data-courtesy-remaining]');
    if (courtesyOutput) courtesyOutput.textContent = String(courtesyRemaining);
    const mapPaidOutput = root.querySelector('[data-map-paid-remaining]');
    if ('paid_limited' in data) root.dataset.paidLimited = data.paid_limited ? '1' : '0';
    if (mapPaidOutput && 'paid_remaining' in data) {
      mapPaidOutput.textContent = data.paid_remaining === null
        ? (data.allowance_display?.paid_display || '∞') : String(Number(data.paid_remaining || 0));
    }
    const courtesyNotice = root.querySelector('[data-courtesy-notice]');
    if (courtesyNotice) courtesyNotice.classList.toggle('d-none', courtesyRemaining <= 0);
    if (data.allowance_display) {
      const mapCourtesyBadge = root.querySelector('[data-courtesy-badge]');
      if (mapCourtesyBadge) mapCourtesyBadge.classList.toggle('d-none', data.allowance_display.courtesy_limit <= 0);
      const button = document.getElementById(`seat_select_button_${root.dataset.ticketClass}`);
      if (button) button.disabled = root.dataset.paidLimited === '1'
        && Number(data.paid_remaining) <= 0 && courtesyRemaining <= 0 && !selected.size;
    }
    const allowanceSummary = document.getElementById(`seat_allowance_${root.dataset.ticketClass}`);
    if (allowanceSummary && data.allowance_display) {
      const paidOutput = allowanceSummary.querySelector('[data-paid-allowance]');
      const remainingOutput = allowanceSummary.querySelector('[data-courtesy-allowance]');
      const courtesyBadge = allowanceSummary.querySelector('[data-session-courtesy-badge]');
      if (paidOutput) paidOutput.textContent = data.allowance_display.paid_display;
      if (remainingOutput) remainingOutput.textContent = data.allowance_display.courtesy_display;
      if (courtesyBadge) courtesyBadge.classList.toggle('d-none', data.allowance_display.courtesy_limit <= 0);
    }
    serverOffset = Date.parse(data.server_now) - Date.now();
    renderState();
    renderCountdown();
    return true;
  }

  async function refreshStatus() {
    const version = ++statusVersion;
    try {
      const response = await fetch(root.dataset.statusUrl, {headers: {'X-Requested-With': 'XMLHttpRequest'}});
      if (!response.ok) {
        if (response.status === 409) {
          const error = await response.json();
          feedback.textContent = error.error;
          statusReady = false;
        }
        return false;
      }
      const data = await response.json();
      if (version !== statusVersion) return false;
      if (data.redirect) {
        window.location.assign(data.redirect);
        return false;
      }
      return applyStatus(data);
    } catch (_) { return false; /* A próxima atualização tenta novamente. */ }
  }
  renderState();
  renderCountdown();
  if (readOnly) return;
  poller = window.CMPSeatPolling?.register(root, applyStatus);
  if (!poller) refreshStatus();
  if (!poller) root._seatStatusTimer = window.setInterval(() => {
    if (!root.isConnected) { window.clearInterval(root._seatStatusTimer); window.clearInterval(root._seatCountdownTimer); return; }
    if (!pending.size && !document.hidden) return refreshStatus();
  }, 5000);
  root._seatCountdownTimer = window.setInterval(() => {
    if (!root.isConnected) { window.clearInterval(root._seatCountdownTimer); return; }
    renderCountdown();
  }, 1000);
};

document.addEventListener('htmx:beforeCleanupElement', event => {
  event.detail.elt?._seatNavigationCleanup?.();
});

// A failed initial GET must not leave a permanent spinner (the HTMX trigger is once).
document.addEventListener('htmx:responseError', event => {
  const target = event.detail.target;
  if (!target?.dataset.mapError || target.querySelector('.seat-map-root')) return;
  const message = document.createElement('p'); message.className = 'alert alert-warning';
  message.textContent = target.dataset.mapError;
  const retry = document.createElement('button'); retry.type = 'button';
  retry.className = 'btn btn-outline-dark'; retry.textContent = target.dataset.mapRetry;
  retry.addEventListener('click', async () => {
    retry.disabled = true;
    try { await htmx.ajax('GET', target.getAttribute('hx-get'), {target, swap: 'innerHTML'}); }
    finally { retry.disabled = false; }
  });
  target.replaceChildren(message, retry);
});
